<template>
  <div>
    <!-- Projects Gallery VIEW -->
    <div v-if="view==='gallery'">
      <v-card class="pa-6 glass-card" style="max-width:1050px;margin:44px auto;">
        <h2 class="section-title mb-4">🎬 Story Projects Gallery</h2>
        <v-btn color="success" class="mb-4" @click="newProject">+ New Project</v-btn>
        <v-row dense>
          <v-col v-for="proj in projects" :key="proj.id" cols="12" md="6" xl="4">
            <v-card class="pa-4 mb-3 glass-card2">
              <div class="flex-between mb-2">
                <span class="scene-title">{{ proj.name }}</span>
                <div>
                  <v-btn icon color="primary" @click="openProjectById(proj.id)">
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                  <v-btn icon color="success" @click="renameProject(proj)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="error" @click="deleteProject(proj.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
              <div class="mb-2">
                <span class="label">Last saved:</span>
                {{ new Date(proj.lastSaved).toLocaleString() }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <!-- Project Dashboard VIEW -->
    <div v-else>
      <div class="dashboard-grid">
        <!-- Sidebar -->
        <aside class="sidebar">
          <h1 class="logo" style="cursor:pointer;" @click="backToGallery">🎬 SceneGen Studio</h1>
          <v-text-field
            v-model="project.name"
            label="Project Name"
            dense
            outlined
            class="project-title"
            :counter="60"
          ></v-text-field>
          <v-btn block color="primary" @click="showSection = 'scene'">Scene</v-btn>
          <v-btn block color="primary" @click="showSection = 'shots'">Shots</v-btn>
          <v-btn block color="primary" @click="showSection = 'characters'">Characters</v-btn>
          <v-btn block color="success" @click="saveProject">💾 Save Project</v-btn>
          <v-btn block color="info" @click="exportProject">Export Project</v-btn>
          <v-btn block color="warning" @click="backToGallery">Back to Gallery</v-btn>
          <div class="spacer"></div>
        </aside>

        <!-- Main Content -->
        <main class="main-content">
          <!-- SCENE PANEL -->
          <section v-if="showSection==='scene'" class="scene-section">
            <v-card color="primary2" class="pa-5 mb-4 glass-card">
              <div class="flex-between">
                <h2 class="section-title">Scenes in <span style="color:#aef">{{ project.name || 'Untitled Project' }}</span></h2>
                <v-btn small color="success" @click="addNewScene">+ Add New Scene</v-btn>
              </div>
              <v-select
                v-model="currentSceneIndex"
                :items="sceneDropdown"
                label="Switch Scene"
                dense
                outlined
                class="mb-4"
              ></v-select>
              <v-text-field
                v-model="currentScene.name"
                label="Scene Name"
                dense
                outlined
                class="scene-title-field"
                :counter="50"
              ></v-text-field>
              <v-form @submit.prevent="generateScene">
                <v-text-field
                  v-model="currentScene.prompt"
                  label="Describe this scene"
                  clearable
                  required
                  multi-line
                  class="mb-3 inputarea"
                  placeholder="E.g., 'A detective interrogates a mysterious suspect in a neon-lit 1980s city at midnight.'"
                ></v-text-field>
                <v-btn color="primary" type="submit" :loading="loadingScene" class="mb-4">
                  Generate Full Scene
                </v-btn>
                <v-btn color="grey" v-if="currentScene.script" @click="generateScene">
                  Regenerate
                </v-btn>
                <v-btn color="error" v-if="project.scenes.length>1" @click="deleteCurrentScene">
                  Delete Scene
                </v-btn>
              </v-form>
              <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
              <div v-if="currentScene.script" class="script-block">
                <h3>📝 Scene Script</h3>
                <pre class="scene-script">{{ currentScene.script }}</pre>
              </div>
            </v-card>
          </section>
          <!-- SHOTS PANEL -->
          <section v-if="showSection==='shots'" class="shots-section">
            <v-card class="pa-5 mb-4 glass-card">
              <div class="flex-between">
                <h2 class="section-title">Shots in Scene: <span style="color:#b6f">{{ currentScene.name || 'Untitled Scene' }}</span></h2>
                <v-btn small color="success" @click="addShot">+ Add Shot</v-btn>
              </div>
              <v-row dense>
                <v-col v-for="(shot, i) in currentScene.sceneBreakdown" :key="i" cols="12" md="6" xl="4">
                  <v-card class="pa-3 shot-card glass-card2">
                    <div class="flex-between">
                      <h4 class="mb-2">Shot {{ i+1 }}</h4>
                      <v-btn icon small color="error" @click="deleteShot(i)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <v-textarea
                      v-model="shot.description"
                      label="Shot Description"
                      outlined
                      dense
                      auto-grow
                      class="mb-2"
                    ></v-textarea>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </section>
          <!-- CHARACTERS PANEL -->
          <section v-if="showSection==='characters'" class="characters-section">
            <v-card class="pa-5 mb-4 glass-card">
              <div class="flex-between">
                <h2 class="section-title">Character Profiles</h2>
                <v-btn small color="success" @click="addCharacter">+ Add Character</v-btn>
              </div>
              <v-row dense>
                <v-col v-for="(char, i) in currentScene.characters" :key="i" cols="12" md="6" xl="4">
                  <v-card class="pa-3 char-card glass-card2">
                    <div class="flex-between">
                      <h4 class="mb-2">{{ char.name }}</h4>
                      <v-btn icon small color="error" @click="deleteCharacter(i)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <v-text-field
                      v-model="char.name"
                      label="Name"
                      dense
                      outlined
                      class="mb-2"
                    />
                    <v-textarea
                      v-model="char.description"
                      label="Character Description"
                      outlined
                      dense
                      auto-grow
                    ></v-textarea>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SceneGenDashboard",
  data() {
    return {
      view: 'gallery', // Switch between 'gallery' and 'dashboard'
      projects: [],
      project: {
        name: "Untitled Project",
        id: null,
        scenes: [
          { name: "Scene 1", prompt: "", script: "", sceneBreakdown: [], characters: [] }
        ]
      },
      currentSceneIndex: 0,
      showSection: "scene",
      loadingScene: false,
      error: "",
    };
  },
  computed: {
    sceneDropdown() {
      return this.project.scenes.map((s, i) => ({ text: s.name || `Scene ${i+1}`, value: i }));
    },
    currentScene: {
      get() {
        return this.project.scenes[this.currentSceneIndex];
      },
      set(val) {
        this.$set(this.project.scenes, this.currentSceneIndex, val);
      }
    }
  },
  created() {
    this.loadProjectsIndex();
  },
  methods: {
    // GALLERY LOAD/SAVE/CRUD
    loadProjectsIndex() {
      this.projects = JSON.parse(localStorage.getItem('scenegen_projects_index') || '[]');
    },
    openProjectById(id) {
      const saved = localStorage.getItem(`scenegen_project_${id}`);
      if (saved) {
        this.project = JSON.parse(saved);
        this.project.id = id;
        this.currentSceneIndex = 0;
        this.showSection = "scene";
        this.view = 'dashboard'; // Switch to dashboard!
        // Ensure at least one scene
        if (!this.project.scenes || !this.project.scenes.length) {
          this.project.scenes = [
            { name: "Scene 1", prompt: "", script: "", sceneBreakdown: [], characters: [] }
          ];
        }
      } else {
        alert("Project not found!");
      }
    },
    newProject() {
      const name = prompt("Name your new project:", "Untitled Project");
      if (!name) return;
      const id = Date.now().toString();
      const now = Date.now();
      const projectMeta = { id, name, lastSaved: now };
      let projects = JSON.parse(localStorage.getItem('scenegen_projects_index') || '[]');
      projects.push(projectMeta);
      localStorage.setItem('scenegen_projects_index', JSON.stringify(projects));
      // Save empty project object
      const newProjObj = {
        name,
        id,
        scenes: [
          { name: "Scene 1", prompt: "", script: "", sceneBreakdown: [], characters: [] }
        ]
      };
      localStorage.setItem(`scenegen_project_${id}`, JSON.stringify(newProjObj));
      this.openProjectById(id); // Immediately open
      this.loadProjectsIndex();
    },
    deleteProject(id) {
      if (!confirm("Delete this project?")) return;
      let projects = JSON.parse(localStorage.getItem('scenegen_projects_index') || '[]');
      projects = projects.filter(p => p.id !== id);
      localStorage.removeItem(`scenegen_project_${id}`);
      localStorage.setItem('scenegen_projects_index', JSON.stringify(projects));
      this.loadProjectsIndex();
    },
    renameProject(projectMeta) {
      const newName = prompt("Rename project:", projectMeta.name);
      if (newName && newName !== projectMeta.name) {
        // Update meta
        projectMeta.name = newName;
        let projects = JSON.parse(localStorage.getItem('scenegen_projects_index') || '[]');
        projects = projects.map(p => (p.id === projectMeta.id ? { ...p, name: newName } : p));
        localStorage.setItem('scenegen_projects_index', JSON.stringify(projects));
        // Update actual project data if loaded
        const proj = JSON.parse(localStorage.getItem(`scenegen_project_${projectMeta.id}`) || '{}');
        proj.name = newName;
        localStorage.setItem(`scenegen_project_${projectMeta.id}`, JSON.stringify(proj));
        this.loadProjectsIndex();
        // Also update current project if editing it
        if (this.project && this.project.id === projectMeta.id) {
          this.project.name = newName;
        }
      }
    },
    backToGallery() {
      this.view = 'gallery';
      this.loadProjectsIndex();
    },
    // DASHBOARD: Save/Export
    saveProject() {
      if (!this.project.id) {
        this.project.id = Date.now().toString();
      }
      // Update projects index meta
      let projectsIndex = JSON.parse(localStorage.getItem('scenegen_projects_index') || '[]');
      let metaIndex = projectsIndex.findIndex(p => p.id === this.project.id);
      if (metaIndex === -1) {
        projectsIndex.push({ id: this.project.id, name: this.project.name, lastSaved: Date.now() });
      } else {
        projectsIndex[metaIndex].name = this.project.name;
        projectsIndex[metaIndex].lastSaved = Date.now();
      }
      localStorage.setItem('scenegen_projects_index', JSON.stringify(projectsIndex));
      // Save actual project
      localStorage.setItem(`scenegen_project_${this.project.id}`, JSON.stringify(this.project));
      alert('Project saved!');
    },
    exportProject() {
      if (!this.project) return;
      const json = JSON.stringify(this.project, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.project.name.replace(/\s+/g, '_') || 'scenegen-project'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // SCENES/SHOTS/CHARACTERS CRUD
    addNewScene() {
      const newName = `Scene ${this.project.scenes.length + 1}`;
      this.project.scenes.push({
        name: newName,
        prompt: "",
        script: "",
        sceneBreakdown: [],
        characters: [],
      });
      this.currentSceneIndex = this.project.scenes.length - 1;
    },
    deleteCurrentScene() {
      if (this.project.scenes.length === 1) return;
      if (confirm("Delete this scene?")) {
        this.project.scenes.splice(this.currentSceneIndex, 1);
        this.currentSceneIndex = 0;
      }
    },
    addShot() {
      this.currentScene.sceneBreakdown.push({
        description: "New shot description.",
        storyboardPrompt: "",
        visualKeywords: [],
      });
    },
    deleteShot(i) {
      this.currentScene.sceneBreakdown.splice(i, 1);
    },
    addCharacter() {
      this.currentScene.characters.push({
        name: `Character ${this.currentScene.characters.length + 1}`,
        description: "",
      });
    },
    deleteCharacter(i) {
      this.currentScene.characters.splice(i, 1);
    },
    // Placeholder for AI scene generation
    async generateScene() {
      this.error = "";
      this.loadingScene = true;
      this.currentScene.script = "";
      this.currentScene.sceneBreakdown = [];
      this.currentScene.characters = [];
      try {
        await new Promise(r => setTimeout(r, 1200)); // mock API delay
        this.currentScene.script = "Sample script generated by AI...";
        this.currentScene.sceneBreakdown = [
          { description: "Wide shot of neon-lit alley at night.", storyboardPrompt: "A neon-lit alleyway at midnight, rain-slicked streets, shadows, cinematic, moody", visualKeywords: ["alley", "rain", "neon"] }
        ];
        this.currentScene.characters = [
          { name: "Detective Riley", description: "Hard-boiled detective, trenchcoat, sharp eyes." }
        ];
      } catch (err) {
        this.error = "AI failed to generate the scene. " + (err.message || "");
      } finally {
        this.loadingScene = false;
      }
    }
  }
};
</script>


<style scoped>
.glass-card {
  background: rgba(24, 34, 54, 0.88);
  border: 1.5px solid #5eeaff44;
  border-radius: 20px;
  box-shadow: 0 6px 32px #49e5fa11, 0 1px 8px #91e2ff18;
  backdrop-filter: blur(10px) saturate(1.4);
}

.glass-card2 {
  background: rgba(32, 47, 71, 0.85);
  border: 1.1px solid #75d1ff33;
  border-radius: 15px;
  box-shadow: 0 2px 8px #29e4fb22;
  backdrop-filter: blur(5px) saturate(1.18);
}


.section-title, .scene-title {
  font-family: 'Orbitron', 'Fira Mono', monospace;
  color: #93e0ff;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 10px #0097fb19;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.project-title {
  font-size: 1.21em;
  font-weight: bold;
  color: #b6e3fc !important;
  margin-bottom: 12px;
  background: rgba(80,120,170,0.09) !important;
  border-radius: 8px;
  border: 1.3px solid #43b5fd21 !important;
  box-shadow: 0 2px 16px #67ecff19;
}

.scene-title-field {
  font-size: 1.12em;
  color: #d8f3ff !important;
  font-weight: 600;
}

/* --- FUTURISTIC MOVIE STUDIO STYLE --- */
.dashboard-grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  min-height: 100vh;
  background: linear-gradient(120deg,#181b29 60%,#324069 100%);
}
.sidebar {
  background: rgba(18,23,42,0.98);
  padding: 30px 16px 14px 16px;
  color: #eaf6ff;
  display: flex;
  flex-direction: column;
  border-right: 2.5px solid #2968adbb;
  min-height: 100vh;
  min-width: 220px;
  box-shadow: 0 0 48px #00eeff16, 0 0 0 #2bf8f944;
  z-index: 3;
}
.logo {
  font-family: 'Orbitron', monospace;
  font-size: 1.65rem;
  margin-bottom: 30px;
  color: #66e5fa;
  text-shadow: 0 0 14px #0097fb77;
  letter-spacing: 1.2px;
}
.sidebar .v-btn {
  margin-bottom: 17px;
  font-weight: 600;
}
.spacer {
  flex-grow: 1;
}
.main-content {
  padding: 42px 3vw 46px 3vw;
  min-width: 0;
}
.section-title {
  font-family: 'Orbitron', monospace;
  color: #67f2ff;
  font-size: 1.5rem;
  margin-bottom: 18px;
  letter-spacing: 1px;
}
.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.script-block {
  background: rgba(24,34,60,0.97);
  border-radius: 13px;
  margin: 0.7em 0 0 0;
  padding: 1em 1em;
  color: #b6eaff;
  font-size: 1.11em;
  font-family: 'Fira Mono', 'Roboto Mono', monospace;
  box-shadow: 0 0 16px #38affb1c;
  border: 1.5px solid #21c4e7;
}
.shot-card, .char-card, .glass-card, .glass-card2 {
  background: rgba(32,41,71,0.87) !important;
  border-radius: 17px !important;
  box-shadow: 0 0 22px #0097fb44, 0 1.5px 0 #19e3ff33;
  margin-bottom: 25px;
  border: 1.5px solid #289cf4bb;
}
.glass-card2 {
  background: rgba(36,52,91,0.79) !important;
  border: 1.5px solid #2ac2f7aa;
}
.inputarea {
  background: rgba(32,41,71,0.94) !important;
  border-radius: 8px !important;
  color: #aef;
  font-size: 1.25rem !important;
}
.storyboard-label {
  font-size: 0.96rem;
  font-weight: 600;
  color: #74e4ff;
  margin-top: 8px;
}
.image-holder {
  margin-top: 8px;
  margin-bottom: 7px;
  text-align: center;
}
.storyboard-image {
  margin-top: 7px;
  margin-bottom: 6px;
  border-radius: 11px;
  box-shadow: 0 0 14px #1bfbfb44;
  max-width: 100%;
  display: block;
}
.char-image-row {
  display: flex;
  flex-wrap: wrap;
  gap: 11px;
  margin: 8px 0 0 0;
  align-items: flex-end;
}
.char-image {
  width: 130px;
  height: 130px;
  object-fit: cover;
  border-radius: 11px;
  box-shadow: 0 0 13px #2cfbfb55;
  border: 2.5px solid #3cc3ff33;
  cursor: pointer;
  margin-right: 7px;
  transition: border 0.2s, transform 0.18s;
}
.char-image.main-img, .char-image.large {
  border: 3.5px solid #65ecff;
  box-shadow: 0 0 22px #31dfff61;
  transform: scale(1.08);
}
.char-image.large {
  width: 170px;
  height: 170px;
  margin: 0 auto 12px auto;
  display: block;
}
.caption {
  display: block;
  font-size: 0.95em;
  color: #bbefff;
  margin-top: 3px;
}
@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .sidebar {
    min-width: 0;
    padding-bottom: 5vw;
  }
  .main-content {
    padding: 20px 2vw 22px 2vw;
  }
}
</style>
<style scoped>
.scene-title {
  font-weight: bold;
  color: #53c6fa;
  font-size: 1.12rem;
  letter-spacing: 1px;
}
.label {
  color: #77d9fa;
  font-weight: 600;
  margin-right: 6px;
}
.glass-card {
  background: rgba(24, 34, 54, 0.88);
  border: 1.5px solid #5eeaff44;
  border-radius: 20px;
  box-shadow: 0 6px 32px #49e5fa11, 0 1px 8px #91e2ff18;
  backdrop-filter: blur(10px) saturate(1.4);
}

.glass-card2 {
  background: rgba(32, 47, 71, 0.85);
  border: 1.1px solid #75d1ff33;
  border-radius: 15px;
  box-shadow: 0 2px 8px #29e4fb22;
  backdrop-filter: blur(5px) saturate(1.18);
}


.section-title, .scene-title {
  font-family: 'Orbitron', 'Fira Mono', monospace;
  color: #93e0ff;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 10px #0097fb19;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.project-title {
  font-size: 1.21em;
  font-weight: bold;
  color: #b6e3fc !important;
  margin-bottom: 12px;
  background: rgba(80,120,170,0.09) !important;
  border-radius: 8px;
  border: 1.3px solid #43b5fd21 !important;
  box-shadow: 0 2px 16px #67ecff19;
}

.scene-title-field {
  font-size: 1.12em;
  color: #d8f3ff !important;
  font-weight: 600;
}

</style>
